import { useState, useRef } from 'react';
import { Camera, X, Loader2, AlertCircle, Image, ZoomIn } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { contractorJobService } from '../../../services/contractorJobService';
import { generateThumbnail } from '../../../utils/imageUtils';

/**
 * PhotoUploader - Mobile-optimized photo upload component
 *
 * Features:
 * - Batch upload (multiple files at once)
 * - Individual photo removal
 * - Upload progress indication
 * - Camera capture prioritized on mobile
 * - Photo count in header
 * - Date/time stamp watermark on camera photos
 * - Scrollable photo grid (max 3 rows visible)
 * - Photo preview popup on click
 */
export default function PhotoUploader({
  instanceId,
  photoType = 'before',
  photos = [],
  onPhotosChange,
  required = false,
  title = 'Photos',
  description = 'Upload photos',
  disabled = false
}) {
  const [uploading, setUploading] = useState([]);
  const [error, setError] = useState('');
  const [previewPhoto, setPreviewPhoto] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  /**
   * Compress and resize an image file
   * Resizes to max 2048px dimension and compresses to 80% JPEG quality
   * Returns a new File with reduced size
   */
  const compressImage = (file, maxDimension = 2048, quality = 0.80) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Calculate new dimensions maintaining aspect ratio
          let { width, height } = img;
          if (width > maxDimension || height > maxDimension) {
            if (width > height) {
              height = Math.round((height * maxDimension) / width);
              width = maxDimension;
            } else {
              width = Math.round((width * maxDimension) / height);
              height = maxDimension;
            }
          }

          // Set canvas size to new dimensions
          canvas.width = width;
          canvas.height = height;

          // Draw resized image
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(compressedFile);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for compression'));
      };

      // Load image from file
      img.src = URL.createObjectURL(file);
    });
  };

  /**
   * Add date/time stamp watermark to an image file
   * Returns a new File with the timestamp embedded
   */
  const addTimestampToImage = (file) => {
    return new Promise((resolve, reject) => {
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');

          // Set canvas size to match image
          canvas.width = img.width;
          canvas.height = img.height;

          // Draw the original image
          ctx.drawImage(img, 0, 0);

          // Format timestamp
          const now = new Date();
          const timestamp = now.toLocaleString('en-AU', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
            hour12: true
          });

          // Calculate font size based on image dimensions (responsive)
          const fontSize = Math.max(Math.min(img.width, img.height) * 0.035, 16);
          const padding = fontSize * 0.5;

          // Set up text style
          ctx.font = `bold ${fontSize}px Arial, sans-serif`;
          ctx.textAlign = 'right';
          ctx.textBaseline = 'bottom';

          // Measure text for background
          const textMetrics = ctx.measureText(timestamp);
          const textWidth = textMetrics.width;
          const textHeight = fontSize * 1.2;

          // Position in bottom-right corner
          const x = img.width - padding;
          const y = img.height - padding;

          // Draw semi-transparent background
          ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
          ctx.fillRect(
            x - textWidth - padding,
            y - textHeight,
            textWidth + padding * 2,
            textHeight + padding * 0.5
          );

          // Draw timestamp text
          ctx.fillStyle = '#FFFFFF';
          ctx.fillText(timestamp, x, y);

          // Convert canvas to blob
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // Create new file with same name
                const stampedFile = new File([blob], file.name, {
                  type: 'image/jpeg',
                  lastModified: Date.now()
                });
                resolve(stampedFile);
              } else {
                reject(new Error('Failed to create stamped image'));
              }
            },
            'image/jpeg',
            0.92 // Quality
          );
        } catch (err) {
          reject(err);
        }
      };

      img.onerror = () => {
        reject(new Error('Failed to load image for stamping'));
      };

      // Load image from file
      img.src = URL.createObjectURL(file);
    });
  };

  /**
   * Handle camera photo capture - adds timestamp before upload
   */
  const handleCameraCapture = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setError('');

    // Create upload placeholders
    const uploadIds = files.map((_, idx) => `upload-${Date.now()}-${idx}`);
    setUploading(prev => [...prev, ...uploadIds]);

    // Process and upload files
    const results = await Promise.allSettled(
      files.map(async (file) => {
        try {
          // First compress the image, then add timestamp watermark
          const compressedFile = await compressImage(file);
          const stampedFile = await addTimestampToImage(compressedFile);

          // Generate thumbnail (200px) for grid display
          const thumbnailFile = await generateThumbnail(stampedFile);

          // Upload to server (with thumbnail)
          const photoRecord = await contractorJobService.uploadPhoto(instanceId, stampedFile, photoType, thumbnailFile);

          // Get signed URLs for display
          const { url, thumbnailUrl } = await contractorJobService.getPhotoUrls(photoRecord.storage_path, photoRecord.thumbnail_path);

          return {
            id: photoRecord.id,
            url,
            thumbnailUrl,
            storagePath: photoRecord.storage_path,
            thumbnailPath: photoRecord.thumbnail_path,
            photoType: photoRecord.photo_type,
            uploadedAt: photoRecord.uploaded_at
          };
        } catch (err) {
          throw new Error(`Failed to upload photo: ${err.message}`);
        }
      })
    );

    // Process results
    const successfulUploads = [];
    const errors = [];

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        successfulUploads.push(result.value);
      } else {
        errors.push(result.reason.message);
      }
    });

    // Update photos state
    if (successfulUploads.length > 0) {
      onPhotosChange([...photos, ...successfulUploads]);
    }

    // Show errors if any
    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    // Clear uploading state
    setUploading([]);

    // Reset camera input
    if (cameraInputRef.current) cameraInputRef.current.value = '';
  };

  /**
   * Check if a file was freshly captured (taken just now with camera)
   * Files captured via camera typically have lastModified within last 10 seconds
   */
  const isFreshlyCaptured = (file) => {
    const now = Date.now();
    const fileAge = now - file.lastModified;
    // If file was modified less than 10 seconds ago, it's likely freshly captured
    return fileAge < 10000;
  };

  /**
   * Handle gallery/file selection - adds timestamp to freshly captured photos
   */
  const handleFileSelect = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setError('');

    // Create upload placeholders
    const uploadIds = files.map((_, idx) => `upload-${Date.now()}-${idx}`);
    setUploading(prev => [...prev, ...uploadIds]);

    // Upload files in parallel (compress all, add timestamp only to freshly captured photos)
    const results = await Promise.allSettled(
      files.map(async (file) => {
        try {
          // First compress the image
          const compressedFile = await compressImage(file);

          // Check if this is a freshly captured photo (taken via camera option in file picker)
          const needsTimestamp = isFreshlyCaptured(file);
          const fileToUpload = needsTimestamp ? await addTimestampToImage(compressedFile) : compressedFile;

          // Generate thumbnail (200px) for grid display
          const thumbnailFile = await generateThumbnail(fileToUpload);

          // Upload to server (with thumbnail)
          const photoRecord = await contractorJobService.uploadPhoto(instanceId, fileToUpload, photoType, thumbnailFile);

          // Get signed URLs for display
          const { url, thumbnailUrl } = await contractorJobService.getPhotoUrls(photoRecord.storage_path, photoRecord.thumbnail_path);

          return {
            id: photoRecord.id,
            url,
            thumbnailUrl,
            storagePath: photoRecord.storage_path,
            thumbnailPath: photoRecord.thumbnail_path,
            photoType: photoRecord.photo_type,
            uploadedAt: photoRecord.uploaded_at
          };
        } catch (err) {
          throw new Error(`Failed to upload ${file.name}: ${err.message}`);
        }
      })
    );

    // Process results
    const successfulUploads = [];
    const errors = [];

    results.forEach((result) => {
      if (result.status === 'fulfilled') {
        successfulUploads.push(result.value);
      } else {
        errors.push(result.reason.message);
      }
    });

    // Update photos state
    if (successfulUploads.length > 0) {
      onPhotosChange([...photos, ...successfulUploads]);
    }

    // Show errors if any
    if (errors.length > 0) {
      setError(errors.join('. '));
    }

    // Clear uploading state
    setUploading([]);

    // Reset file input
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleRemovePhoto = async (e, photoId, storagePath, thumbnailPath) => {
    e.stopPropagation(); // Prevent opening preview when clicking remove
    try {
      await contractorJobService.deletePhoto(photoId, storagePath, thumbnailPath);
      onPhotosChange(photos.filter(p => p.id !== photoId));
    } catch (err) {
      setError(`Failed to remove photo: ${err.message}`);
    }
  };

  const isUploading = uploading.length > 0;
  const totalItems = photos.length + uploading.length;

  return (
    <div className="space-y-3">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h3 className="font-medium text-text-primary">{title}</h3>
          {photos.length > 0 && (
            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-medium rounded-full">
              {photos.length}
            </span>
          )}
          {required && (
            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 text-xs rounded-full">
              Required
            </span>
          )}
        </div>
      </div>

      {/* Description */}
      {description && (
        <p className="text-sm text-text-secondary">{description}</p>
      )}

      {/* Compact Upload Buttons */}
      <div className="flex gap-2">
        {/* Camera Button */}
        <label
          className={cn(
            'flex items-center gap-2 px-3 py-2',
            'border border-border rounded-lg',
            'cursor-pointer transition-all text-sm',
            disabled || isUploading
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:border-primary hover:bg-primary/5 active:bg-primary/10'
          )}
        >
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handleCameraCapture}
            disabled={disabled || isUploading}
            className="hidden"
          />
          <Camera className="w-4 h-4 text-primary" />
          <span className="text-text-primary font-medium">Take Photo</span>
        </label>

        {/* Gallery/File Upload Button */}
        <label
          className={cn(
            'flex items-center gap-2 px-3 py-2',
            'border border-border rounded-lg',
            'cursor-pointer transition-all text-sm',
            disabled || isUploading
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:border-primary hover:bg-primary/5 active:bg-primary/10'
          )}
        >
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            multiple
            onChange={handleFileSelect}
            disabled={disabled || isUploading}
            className="hidden"
          />
          <Image className="w-4 h-4 text-text-secondary" />
          <span className="text-text-primary font-medium">Choose Files</span>
        </label>
      </div>

      {/* Helper text for multiple photos */}
      {photos.length > 0 && !isUploading && (
        <p className="text-xs text-text-tertiary">
          Tap &quot;Take Photo&quot; again to add more photos
        </p>
      )}

      {/* Uploading Indicator */}
      {isUploading && (
        <div className="flex items-center gap-3 p-2 bg-primary/10 border border-primary/20 rounded-lg">
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
          <span className="text-sm text-primary font-medium">
            Uploading {uploading.length} photo{uploading.length > 1 ? 's' : ''}...
          </span>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="flex items-start gap-2 p-2 bg-red-500/10 border border-red-500/20 rounded-lg">
          <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-red-400">{error}</p>
        </div>
      )}

      {/* Photo Grid - Scrollable after 3 rows */}
      {totalItems > 0 && (
        <div className="overflow-y-auto max-h-[264px]">
          <div className="grid grid-cols-4 sm:grid-cols-5 lg:grid-cols-6 gap-2">
            {photos.map((photo) => (
              <div
                key={photo.id}
                onClick={() => setPreviewPhoto(photo)}
                className="relative aspect-square rounded-lg overflow-hidden bg-surface group cursor-pointer"
              >
                <img
                  src={photo.thumbnailUrl || photo.url}
                  alt=""
                  className="w-full h-full object-cover"
                />
                {/* Zoom indicator on hover */}
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                  <ZoomIn className="w-6 h-6 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                </div>
                {/* Remove Button */}
                <button
                  onClick={(e) => handleRemovePhoto(e, photo.id, photo.storagePath, photo.thumbnailPath)}
                  disabled={disabled}
                  className={cn(
                    'absolute top-1 right-1 w-6 h-6',
                    'bg-black/60 hover:bg-red-500 rounded-full',
                    'flex items-center justify-center',
                    'transition-colors z-10'
                  )}
                  aria-label="Remove photo"
                >
                  <X className="w-3 h-3 text-white" />
                </button>
              </div>
            ))}

            {/* Upload placeholders while uploading */}
            {uploading.map((id) => (
              <div
                key={id}
                className="aspect-square rounded-lg bg-surface flex items-center justify-center"
              >
                <Loader2 className="w-5 h-5 text-text-tertiary animate-spin" />
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Photo count indicator when scrollable */}
      {photos.length > 12 && (
        <p className="text-xs text-text-tertiary text-center">
          Scroll to see all {photos.length} photos
        </p>
      )}

      {/* Photo Preview Popup */}
      {previewPhoto && (
        <div
          className="fixed inset-0 z-[100] bg-black/90 flex items-center justify-center p-4"
          onClick={() => setPreviewPhoto(null)}
        >
          {/* Close button */}
          <button
            onClick={() => setPreviewPhoto(null)}
            className="absolute top-4 right-4 w-10 h-10 bg-white/10 hover:bg-white/20 rounded-full flex items-center justify-center transition-colors"
            aria-label="Close preview"
          >
            <X className="w-6 h-6 text-white" />
          </button>

          {/* Image */}
          <img
            src={previewPhoto.url}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Delete button in preview */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              handleRemovePhoto(e, previewPhoto.id, previewPhoto.storagePath, previewPhoto.thumbnailPath);
              setPreviewPhoto(null);
            }}
            disabled={disabled}
            className="absolute bottom-4 left-1/2 -translate-x-1/2 px-4 py-2 bg-red-500 hover:bg-red-600 text-white rounded-lg font-medium transition-colors flex items-center gap-2"
          >
            <X className="w-4 h-4" />
            Remove Photo
          </button>
        </div>
      )}
    </div>
  );
}
